import { useNotifications } from '@sequencehq/core-components'
import omit from 'lodash/omit'
import { useAddTaxRegistration } from 'modules/Customers/hooks/useAddTaxRegistration'
import { useUpdateTaxRegistration } from 'modules/Customers/hooks/useUpdateTaxRegistration'
import { useDeleteTaxRegistration } from 'modules/Customers/hooks/useDeleteTaxRegistration'
import { useMutation } from '@sequencehq/api/utils'
import {
  type Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { isSequenceApiError, isSequenceApiErrorResponse } from '@sequencehq/api'
import { getAvalaraAddressError } from 'modules/Customers/utils/getAvalaraAddressError'
import type { Customer } from 'Customer/types'
import { useCustomerContext } from './useCustomerContext'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { getCustomerAliasError } from 'modules/Customers/utils/getCustomerAliasError'
import { getContactEmailError } from 'modules/Customers/utils/getContactEmailError'
import { TaxRegistrationEntry } from 'Customer/components/CustomerFormFields/types'
import { CountriesAlpha2 } from '@sequencehq/api/commonEnums'

type UpdateCustomer = Dashboardv99990101Api.PutCustomer.UpdateCustomerBody & {
  taxRegistrations: TaxRegistrationEntry[]
}

export type UpdateCustomerErrorType =
  | 'INVALID_AVALARA_ADDRESS'
  | 'CUSTOMER_ALIAS_ALREADY_IN_USE'
  | 'CONTACT_EMAIL_ALREADY_EXISTS'
  | 'OTHER'

export type UpdateCustomerError = {
  type: UpdateCustomerErrorType
  identifiers: string[]
  messages: string[]
}

export const useCustomerRoot = ({
  refetchCustomer,
  refetchCustomerAliases
}: {
  refetchCustomer: () => Promise<void>
  refetchCustomerAliases: () => Promise<void>
}) => {
  const customerCtx = useCustomerContext()
  const { displayNotification, displayPromiseNotification } = useNotifications()
  const updateCustomer = useMutation(dashboardv99990101Client.putCustomer)
  const { addTaxRegistration } = useAddTaxRegistration()
  const { updateTaxRegistration } = useUpdateTaxRegistration()
  const { deleteTaxRegistration } = useDeleteTaxRegistration()

  const deleteCustomerAliases = async (
    customerAliases: Array<{ id: string }>
  ) => {
    await Promise.all(
      customerAliases.map(alias =>
        dashboardv20240509Client.deleteCustomerAlias({ id: alias.id })
      )
    )
  }

  const saveTaxRegistrations = async (
    customerId: string,
    entries: TaxRegistrationEntry[]
  ) => {
    const existingIds = new Set(
      entries.map(e => e.registrationId).filter(Boolean)
    )
    const removedIds = customerCtx.taxRegistrations
      .map(r => r.id)
      .filter(id => !existingIds.has(id))

    const [saveResults, deleteResults] = await Promise.all([
      Promise.all(
        entries
          .filter(entry => entry.identifier.trim())
          .map(entry => {
            // Only US/CA support a state/province; sending a stale one for
            // other countries produces an invalid payload.
            const supportsState = ['US', 'CA'].includes(entry.country)
            const body = {
              taxIdentifier: entry.identifier,
              country: entry.country as CountriesAlpha2,
              state: supportsState ? entry.state : undefined,
              customerId
            }
            return entry.registrationId
              ? updateTaxRegistration(entry.registrationId, body)
              : addTaxRegistration(body)
          })
      ),
      Promise.all(removedIds.map(id => deleteTaxRegistration(id)))
    ])

    const failed = [...saveResults, ...deleteResults].filter(r => !r)
    if (failed.length > 0) {
      displayNotification(
        `Failed to save ${failed.length} tax registration${failed.length > 1 ? 's' : ''} for customer`,
        { type: 'error' }
      )
    }
  }

  const editCustomer = async (
    id: string,
    customer: UpdateCustomer
  ): Promise<{
    customer: Customer | null
    error: UpdateCustomerError | null
  }> => {
    const filterEmptyValues = (obj: object): object => {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([, value]) => value !== '' && value !== undefined)
          .map(([key, value]) =>
            value && typeof value === 'object' && !Array.isArray(value)
              ? [key, filterEmptyValues(value as object)]
              : [key, value]
          )
      )
    }

    /**
     * WARNING: some of these values, like customerAliases, will cause a
     * server error when saving them as empty arrays. It's therefore critical
     * to strip them out when empty!
     */
    const filteredAliases = customer.customerAliases?.filter(Boolean) ?? []
    const customerBody = filterEmptyValues({
      ...omit(customer, ['taxRegistrations', 'customerAliases']),
      ...(filteredAliases.length ? { customerAliases: filteredAliases } : {})
    }) as Dashboardv99990101Api.PutCustomer.UpdateCustomerBody

    try {
      const updateOperation = async () => {
        if (
          customerCtx.customerAliases.length &&
          !customerBody.customerAliases?.length
        ) {
          await deleteCustomerAliases(customerCtx.customerAliases)
        }

        const saveResult = await updateCustomer.mutateAsync({
          id,
          body: customerBody
        })

        await saveTaxRegistrations(id, customer.taxRegistrations)

        await refetchCustomer()
        await refetchCustomerAliases()
        return saveResult
      }

      const saveResult = await displayPromiseNotification(updateOperation(), {
        loading: null,
        success: 'Customer updated',
        error: 'Failed to update customer'
      })

      return { customer: saveResult, error: null }
    } catch (e) {
      let errorType: UpdateCustomerErrorType = 'OTHER'
      let identifiers: string[] = []

      const customerAliasError = getCustomerAliasError(e)

      if (customerAliasError.hasError) {
        errorType = 'CUSTOMER_ALIAS_ALREADY_IN_USE'
        identifiers = customerAliasError.identifiers
      }

      if (getContactEmailError(e).hasError) {
        errorType = 'CONTACT_EMAIL_ALREADY_EXISTS'
      }

      if (getAvalaraAddressError(e)) {
        errorType = 'INVALID_AVALARA_ADDRESS'
      }

      const messages: string[] = []
      if (isSequenceApiError(e) && isSequenceApiErrorResponse(e.response)) {
        messages.push(
          ...e.response.error.errors.map(err => err.message).filter(Boolean)
        )
      }

      return {
        customer: null,
        error: {
          type: errorType,
          identifiers,
          messages
        }
      }
    }
  }

  return {
    functions: {
      editCustomer
    }
  }
}
